import { DateAdapter, MatDateFormats } from '@angular/material/core';
import { inject, Injectable, Injector } from '@angular/core';
import { addDays, format, Locale } from 'date-fns';
import { MmCalendarViewState } from '../mm-calendar-view-state';

export const CUSTOM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'P',
  },
  display: {
    dateInput: 'P',
    monthYearLabel: 'MMMM yyyy',
    dateA11yLabel: 'PP',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

class DecoratorDateAdapter<D, L = any> extends DateAdapter<D, L> {
  private readonly injector = inject(Injector);
  protected readonly delegate: DateAdapter<D, L> = this.injector.get(DateAdapter, null, { skipSelf: true })!;

  override getYear(date: D): number {
    return this.delegate.getYear(date);
  }

  override getMonth(date: D): number {
    return this.delegate.getMonth(date);
  }

  override getDate(date: D): number {
    return this.delegate.getDate(date);
  }

  override getYearName(date: D): string {
    return this.delegate.getYearName(date);
  }

  addCalendarMonths(date: D, months: number): D {
    return this.delegate.addCalendarMonths(date, months);
  }

  addCalendarYears(date: D, years: number): D {
    return this.delegate.addCalendarYears(date, years);
  }

  createDate(year: number, month: number, date: number): D {
    return this.delegate.createDate(year, month, date);
  }

  getFirstDayOfWeek(): number {
    return this.delegate.getFirstDayOfWeek();
  }

  getNumDaysInMonth(date: D): number {
    return this.delegate.getNumDaysInMonth(date);
  }

  getDateNames(): string[] {
    return this.delegate.getDateNames();
  }

  override getDayOfWeek(date: D): number {
    return this.delegate.getDayOfWeek(date);
  }

  override setLocale(locale: L): void {
    this.delegate.setLocale(locale);
  }

  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return this.delegate.getMonthNames(style);
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return this.delegate.getDayOfWeekNames(style);
  }

  override parse(value: any, formatStr: string): D | null {
    return this.delegate.parse(value, formatStr);
  }

  override format(date: D, displayFormat: string): string {
    return this.delegate.format(date, displayFormat);
  }
  override deserialize(value: any): D | null {
    return this.delegate.deserialize(value);
  }
  override isDateInstance(obj: any): boolean {
    return this.delegate.isDateInstance(obj);
  }
  override isValid(date: D): boolean {
    return this.delegate.isValid(date);
  }
  override addCalendarDays(date: D, days: number): D {
    return this.delegate.addCalendarDays(date, days);
  }
  override clone(date: D): D {
    return this.delegate.clone(date);
  }
  override toIso8601(date: D): string {
    return this.delegate.toIso8601(date);
  }
  override invalid(): D {
    return this.delegate.invalid();
  }
  override today(): D {
    return this.delegate.today();
  }
  getDelegateLocale(): L {
    return (this.delegate as unknown as any).locale as L;
  }
}

/**
 * Custom date adapter needed for custom month labels.
 *
 * We display long month label on the calendar view (i.e. October)
 * and short month labels in the other views (OCT.).
 *
 * Works in conjunction with MmCalendarViewState and MmDatepickerHeaderComponent.
 */
@Injectable()
export class CustomDateFnsAdapter extends DecoratorDateAdapter<Date, Locale> {
  private readonly viewState = inject(MmCalendarViewState);

  override getMonthNames(): string[] {
    const formatStr = this.viewState.view() === 'month' ? 'MMMM' : 'MMM';
    return Array.from({ length: 12 }).map((_, i) =>
      format(new Date(2020, i, 1), formatStr, { locale: this.getDelegateLocale() }),
    );
  }

  override getDayOfWeekNames(): string[] {
    const baseDate = new Date(2020, 0, 5);
    return Array.from({ length: 7 }).map((_, i) => {
      const day = format(addDays(baseDate, i), 'EEEE', { locale: this.getDelegateLocale() });
      return day[0].toUpperCase();
    });
  }
}
